import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE = 'Care Tech Compare';
const ORIGIN = 'https://caretechcompare.co.uk';

// Unique title + meta description per public route (fixes duplicate titles/descriptions).
export const SEO_MAP = {
  '/': {
    title: 'Care Tech Compare | Compare Care Technology Suppliers & Products',
    description: 'Compare 500+ care technology products and suppliers in one independent directory. Read genuine reviews, shortlist tools and choose the right care tech with confidence.',
  },
  '/browse': {
    title: 'Browse Care Technology Products & Suppliers | Care Tech Compare',
    description: 'Search and filter 500+ care technology products by category, care setting and price model. Compare suppliers side by side and shortlist the right solution.',
  },
  '/get-started': {
    title: 'Get Started Free | Care Tech Compare',
    description: 'Create your free Care Tech Compare account to message suppliers directly, save shortlists and leave reviews on care technology products.',
  },
  '/how-to-choose': {
    title: 'How to Choose Care Technology: A Buyer’s Guide | Care Tech Compare',
    description: 'A practical, independent guide to choosing care technology - what to compare, the questions to ask suppliers, and how to shortlist without the guesswork.',
  },
  '/compare': {
    title: 'Compare Care Tech Products Side by Side | Care Tech Compare',
    description: 'Put care technology products head to head. Compare features, pricing models, integrations and assurances to find the best fit for your service.',
  },
  '/pricing': {
    title: 'Supplier Listing Plans & Pricing | Care Tech Compare',
    description: 'Simple plans for care technology suppliers who want to be found by care providers actively comparing solutions. See what each listing tier includes.',
  },
  '/for-suppliers': {
    title: 'List Your Product for Care Buyers | Care Tech Compare',
    description: 'Get your care technology product in front of providers who are ready to buy. Claim your listing, showcase your product and rank for the categories that matter.',
  },
  '/for-care-providers': {
    title: 'Free Care Tech Directory for Care Providers | Care Tech Compare',
    description: 'A free, independent way for care providers to discover, compare and review 500+ care technology products - built by people who understand social care.',
  },
  '/for-care-services': {
    title: 'List Your Services for the Care Sector | Care Tech Compare',
    description: 'Promote your legal, finance, IT, recruitment or other care-sector service to care providers all year round with a Care Tech Compare listing.',
  },
  '/for-government': {
    title: 'Care Technology for Local Authorities & Government | Care Tech Compare',
    description: 'Free access for Local Authorities and Government bodies to explore 500+ care technology products in one independent, impartial directory.',
  },
  '/for-membership-orgs': {
    title: 'Care Tech Access for Membership Organisations | Care Tech Compare',
    description: 'NCF, Care England and other membership organisations get free access to compare 500+ care technology products and share member reviews.',
  },
  '/for-industry-specialists': {
    title: 'Care Tech Directory for Consultants & Advisors | Care Tech Compare',
    description: 'Consultants and advisors to social care can compare 500+ care technology products in one place to inform recommendations to their clients.',
  },
  '/common-problems': {
    title: 'Solve Common Social Care Problems With Technology | Care Tech Compare',
    description: 'From medication errors to agency spend and lost enquiries - find the type of care technology that solves your specific operational challenge.',
  },
  '/knowledge-hub': {
    title: 'Care Technology Guides & Insights | Care Tech Compare',
    description: 'Independent guides, buyer’s advice and insights on care technology - helping care providers make informed digital decisions.',
  },
  '/podcast': {
    title: 'The Care Tech Compare Podcast | Care Tech Compare',
    description: 'Hear directly from care technology founders and social care leaders on the Care Tech Compare podcast. Real stories, honest insight.',
  },
  '/webinars': {
    title: 'Free Care Technology Webinars | Care Tech Compare',
    description: 'Join free, independent webinars on choosing and selling care technology - practical sessions for care providers and suppliers.',
  },
  '/advisor': {
    title: 'Get a Free Care Tech Shortlist | Care Tech Compare',
    description: 'Tell us what you need and get an independent, tailored shortlist of care technology products suited to your service - free of charge.',
  },
  '/procurement': {
    title: 'Care Technology Procurement Support | Care Tech Compare',
    description: 'Streamline your care technology procurement with independent comparisons, structured evaluations and evidence you can trust.',
  },
  '/procurement/insurance': {
    title: 'The Insurance Value of Care Technology | Care Tech Compare',
    description: 'Understand how the right care technology can reduce risk and support your insurance position across your care service.',
  },
  '/assurance': {
    title: 'Care City Assurance Programme | Care Tech Compare',
    description: 'Independent assurance for care technology products - a structured review of security, interoperability and outcomes care buyers can rely on.',
  },
  '/about': {
    title: 'About Care Tech Compare | Independent Care Technology Directory',
    description: 'Care Tech Compare is an independent directory from the team behind CareTechGuide, built to make choosing care technology simpler and fairer.',
  },
  '/contact': {
    title: 'Contact Us | Care Tech Compare',
    description: 'Get in touch with the Care Tech Compare team - questions about listings, the directory, partnerships or the webinars.',
  },
  '/book-a-call': {
    title: 'Book a Call | Care Tech Compare',
    description: 'Book a call with the Care Tech Compare team to talk through listings, comparisons or how the platform can help your organisation.',
  },
  '/homecare-webinar': {
    title: 'Homecare Technology Webinar | Care Tech Compare',
    description: 'A free webinar helping homecare providers choose the right technology - independent, practical and supplier-free.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Care Tech Compare',
    description: 'How Care Tech Compare collects, uses and protects your personal data.',
  },
  '/terms-and-conditions': {
    title: 'Terms & Conditions | Care Tech Compare',
    description: 'The terms and conditions governing your use of the Care Tech Compare platform.',
  },
  '/acceptable-use-policy': {
    title: 'Acceptable Use Policy | Care Tech Compare',
    description: 'The acceptable use policy for the Care Tech Compare website and directory.',
  },
};

const DEFAULT = {
  title: 'Care Tech Compare | Compare Care Technology Suppliers & Products',
  description: 'Compare care technology suppliers and products in one independent directory. Read reviews, shortlist tools and choose the right care tech with confidence.',
};

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Sets a unique document title, meta description, canonical URL and OG/Twitter tags
 * for the current route. Rendered once inside the public layout. Static routes use the
 * SEO_MAP; dynamic detail pages can still override the title themselves.
 */
export default function RouteSeo() {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = SEO_MAP[pathname] || DEFAULT;
    const url = `${ORIGIN}${pathname === '/' ? '' : pathname}`;
    document.title = meta.title;
    upsertMeta('name', 'description', meta.description);
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', meta.description);
    upsertLink('canonical', url);
  }, [pathname]);
  return null;
}

export { SITE, ORIGIN };

/**
 * Override the social-preview meta for a dynamic detail page (supplier/product).
 * Sets og/twitter title, description, url and a tailored share image. Runs after
 * the page's data loads so it wins over the generic RouteSeo defaults.
 */
export function setSocialPreview({ image, title, description, url }) {
  if (title) {
    upsertMeta('property', 'og:title', title);
    upsertMeta('name', 'twitter:title', title);
  }
  if (description) {
    upsertMeta('property', 'og:description', description);
    upsertMeta('name', 'twitter:description', description);
  }
  if (url) upsertMeta('property', 'og:url', url);
  if (image) {
    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:image', image);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
  }
}

/** Injects (and cleans up) a JSON-LD <script> in <head>, keyed by id. */
export function JsonLd({ id, data }) {
  const json = JSON.stringify(data);
  useEffect(() => {
    const sid = `jsonld-${id}`;
    let el = document.getElementById(sid);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = sid;
      document.head.appendChild(el);
    }
    el.textContent = json;
    return () => { const e = document.getElementById(sid); if (e) e.remove(); };
  }, [id, json]);
  return null;
}

/** Build FAQPage structured data from a [{q, a}] list. */
export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: (faqs || []).map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

/** BreadcrumbList from [{name, url}] (url is a site-relative path or omitted for the last item). */
export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: (items || []).map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    ...(it.url ? { item: `${ORIGIN}${it.url}` } : {}),
  })),
});

/** Product structured data. AggregateRating/Review only included when genuine review data exists. */
export const productSchema = (p, s, path) => {
  const d = { '@context': 'https://schema.org', '@type': 'Product', name: p.name, url: `${ORIGIN}${path}` };
  if (p.description) d.description = String(p.description).replace(/<[^>]*>/g, '').slice(0, 500);
  if (p.image) d.image = p.image;
  if (Array.isArray(p.categories) && p.categories.length) d.category = p.categories[0];
  if (s && s.name) d.brand = { '@type': 'Brand', name: s.name };
  const rating = p.rating ?? p.average_rating ?? p.avg_rating;
  const count = p.review_count ?? p.reviews_count ?? (Array.isArray(p.reviews) ? p.reviews.length : 0);
  if (rating && count) {
    d.aggregateRating = { '@type': 'AggregateRating', ratingValue: rating, reviewCount: count };
  }
  if (Array.isArray(p.reviews) && p.reviews.length) {
    d.review = p.reviews.slice(0, 5).map((r) => ({
      '@type': 'Review',
      reviewRating: { '@type': 'Rating', ratingValue: r.rating ?? r.ratingValue },
      author: { '@type': 'Person', name: r.author || r.name || 'Verified user' },
      ...(r.body || r.text ? { reviewBody: r.body || r.text } : {}),
    }));
  }
  return d;
};


